import { Filesystem } from '@capacitor/filesystem';
import {
  IonContent,
  IonPage,
  useIonToast,
  useIonViewDidLeave,
} from '@ionic/react';
import Spacer from 'components/Spacer';
import { IValueDidChange, action, runInAction, observe } from 'mobx';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { useFSM } from '../../services/DongleController/DongleController';
import { getVideoFileInfo, withTimeout } from '../../util';
import PostRecordModal from '../../components/modals/PostRecordModal';
import RecordInfoModal from '../../components/modals/RecordInfoModal';
import TimerElement from './TimerElement';
import useCameraFrame from './useCameraFrame';
import useRecordTimer from './useRecordTimer';
import useRunBaseData from './useRunBaseData';
import Toolbar from 'pages/Record/components/Toolbar';
import { IonIcon } from '@ionic/react';
import { batteryFull, batteryHalf, batteryDead } from 'ionicons/icons';
import { useStores } from 'models';
import DeviceModal from '../../components/modals/DeviceModal';
import { StatesBLE } from 'services/BLE/BLE';
import InputsUser from './InputsUser';
import ContestBanner from './components/ContestBanner';
import { observer } from 'mobx-react-lite';
import { isMockElement } from '../../util/mockDataDetection';
import { useContestSelection } from '../../hooks/useContestSelection';
import { logger } from 'services/Logger';

import './css/Record.css';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

export enum StatesUserInput {
  Idle = 'IDLE',
  Record = 'RECORD',
  Tare = 'TARE',
}

const RecordPage = observer(() => {
  const { t } = useTranslation();
  const fsm = useFSM();
  const [_cameraFrameRef, dimensions] = useMeasure<any | null>();
  const [_toast, _dismiss] = useIonToast();
  const { timer, startTimer, stopTimer, resetTimer } = useRecordTimer(() =>
    toggleRecording()
  );
  const {
    startVideoRecording,
    stopVideoRecording,
    cameraShown,
    activateCamera,
    deactivateCamera,
  } = useCameraFrame(dimensions);

  const [userInputRecording, setUserInputRecording] = useState(false);
  const recordingRef = useRef(false);
  const [canRecord, setCanRecord] = useState(false);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [_debugModalOpen, _setDebugModalOpen] = useState(false);

  const [isPostRecordModalOpen, setPostRecordModalOpen] = useState(false);
  const [isRecordInfoModalOpen, setRecordInfoModalOpen] = useState(false);

  const { createNewRun, baseData } = useRunBaseData();
  const { currentUser, exploreElementStore } = useStores();

  // Contest Selection State
  const contestSelection = useContestSelection();

  // Update Trigger for Contest Banner Re-render
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [_contestDebugInfo, setContestDebugInfo] = useState<
    Record<string, any>
  >({});

  const [debugLoggedContests, setDebugLoggedContests] = useState<Set<string>>(
    new Set()
  );

  const _openDebugModal = () => {
    _setDebugModalOpen(true);
  };

  useEffect(() => {
    window.contestSelectionUpdate = (
      contestId: string,
      runCount: number,
      maxRuns: number
    ) => {
      logger.info(
        `🎯 Contest ${contestId.slice(-6)} updated: ${runCount}/${maxRuns}`
      );

      setContestDebugInfo((prev: Record<string, any>) => ({
        ...prev,
        [contestId]: {
          runCount,
          maxRuns,
          lastUpdate: new Date().toISOString(),
        },
      }));

      setUpdateTrigger((prev) => prev + 1);

      if (
        contestSelection.selectedContestId === contestId &&
        runCount >= maxRuns
      ) {
        logger.info(
          `🏁 Contest ${contestId.slice(-6)} completed - auto-deselecting`
        );
        contestSelection.deselectContest();
      }
    };

    return () => {
      window.contestSelectionUpdate = undefined;
    };
  }, [contestSelection]);

  // Nur aktive Contests anzeigen (startDate <= jetzt <= endDate)
  const userContests = exploreElementStore.elements.filter((item) => {
    if (
      typeof item.type !== 'string' ||
      item.type.toLowerCase() !== 'contest' ||
      isMockElement(item)
    ) {
      return false;
    }
    if (!currentUser?.uid) return false;
    // Zeitliche Filterung: Nur aktive Contests
    const now = new Date();
    const start = new Date(item.startDate);
    const end = new Date(item.endDate);
    if (now < start || now > end) return false;
    return item.isUserParticipating?.(currentUser.uid);
  });

  const getContestStatus = (contest: any) => {
    const contestId = contest.id;

    // ✅ Use MobX views instead of direct property access
    const participant = contest.getParticipant?.(currentUser?.uid);
    const runCount = contest.getParticipantRunCount?.(currentUser?.uid) || 0;
    const bestScore = contest.getParticipantBestScore?.(currentUser?.uid) || 0;

    // ✅ Create participant if participating but no entry exists
    if (!participant && contest.isUserParticipating?.(currentUser?.uid)) {
      // ✅ Use MobX action to create participant
      contest.createContestParticipant?.(
        currentUser?.uid,
        currentUser?.displayName || currentUser?.email || 'Unknown',
        '', // nationality
        currentUser?.photoURL || ''
      );
    }

    const maxRuns = contest.maxRuns || 3;
    const isCompleted = runCount >= maxRuns;
    const scores = participant?.scores || [];

    return {
      isParticipating: true,
      contest,
      position: participant?.position || null,
      runCount,
      maxRuns,
      contestName: contest.name,
      updateRunCount: () => {},
      isCompleted,
      bestScore,
      scores,
      userEntry: participant,
      lastRunAt: participant?.lastRunAt || null,
    };
  };

  useEffect(() => {
    logger.info(`📋 User contests: ${userContests.length} contests available`);
  }, [userContests.length]);

  const closeModal = () => {
    resetTimer();
    setPostRecordModalOpen(false); // ✅ FIXED: Single state
  };

  const openDeviceModal = () => setDeviceModalOpen(true);
  const closeDeviceModal = () => setDeviceModalOpen(false);
  const [present] = useIonToast();

  const toggleVideo = async () => {
    if (cameraShown) {
      await deactivateCamera();
    } else {
      await activateCamera();
    }
  };

  const bleConnected = fsm.bleState === StatesBLE.CONNECTED;

  useEffect(() => {
    return observe(
      fsm.canRecord,
      (change: IValueDidChange<boolean>) =>
        setCanRecord(change.newValue as boolean),
      true
    );
  }, [fsm, setCanRecord]);

  useIonViewDidLeave(() => {
    deactivateCamera();
  });

  const startRecording = async () => {
    logger.info('🟢 Starting recording...');
    const newRun = await createNewRun(cameraShown);

    try {
      if (cameraShown) {
        await withTimeout(2000, 'Recording video timeout', async () => {
          return startVideoRecording(newRun.localVideoFileName!);
        });
      }
    } catch (error) {
      logger.error('❌ Recording start failed:', error);
      try {
        fsm.stopRecording();
        stopVideoRecording();
        try {
          Filesystem.deleteFile(
            getVideoFileInfo(newRun.deviceId, newRun.recordId)
          );
        } catch {}
      } catch (error) {
        logger.error('❌ Recording rollback failed:', error);
      }
      throw error;
    }
  };

  async function stopRecording() {
    logger.info('🟢 Stopping recording...');
    resetTimer();
    stopTimer();
    await stopVideoRecording();
  }

  const showModal = () => {
    logger.info('🟢 Showing post-record modal...');
    let recordSamples = fsm.recordSessions.flat();
    if (recordSamples.length > 18) {
      if (!isPostRecordModalOpen) {
        logger.info(`✅ Enough samples: ${recordSamples.length}`);
        setPostRecordModalOpen(true);
      }
    } else {
      logger.warn(`⚠️ Not enough samples: ${recordSamples.length}`);
      present({
        message: t('ERROR_WHILE_RECORDING_MESSAGE'),
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    }
  };

  const toggleRecording = action('toggleRecording', () => {
    if (recordingRef.current) {
      runInAction(() => {
        fsm.userInputState.set(StatesUserInput.Idle);
        logger.info('🟢 User stopping recording');
        showModal();
        recordingRef.current = false;
        setUserInputRecording(false);
        stopRecording();
      });
    } else {
      runInAction(() => {
        fsm.resetSamples();
        fsm.userInputState.set(StatesUserInput.Record);
        logger.info('🟢 User starting recording');
        startTimer();
        recordingRef.current = true;
        setUserInputRecording(true);
        startRecording();
      });

      setTimeout(
        action('autoStopRecording', () => {
          if (recordingRef.current) {
            logger.info('🟢 Auto-stopping recording');
            toggleRecording();
          }
        }),
        45000
      );
    }
  });

  useEffect(() => {
    recordingRef.current = userInputRecording;
  }, [userInputRecording]);

  const tare = async () => {
    if (fsm.userInputState.get() !== StatesUserInput.Idle) return;
    runInAction(() => {
      fsm.userInputState.set(StatesUserInput.Tare);
    });
    try {
      await new Promise((resolve) => setTimeout(resolve, 3000));
    } catch (error) {
      logger.error('Error' + error);
    } finally {
      runInAction(() => {
        fsm.userInputState.set(StatesUserInput.Idle);
      });
    }
  };

  if (cameraShown) {
    document.body.className = 'transparent';
  } else {
    document.body.className = '';
  }

  const selectedContest =
    userContests.find(
      (contest) => contest.id === contestSelection.selectedContestId
    ) || null;

  // Modal open state
  const recordData = fsm.recordSessions.flat();

  // Determine if the post-record modal should be shown
  const shouldShowPostRecordModal = useMemo(() => {
    // ✅ Nur öffnen wenn Recording wirklich beendet ist, nicht während Live-Stream
    return (
      isPostRecordModalOpen && recordData.length > 0 && !recordingRef.current
    );
  }, [isPostRecordModalOpen, recordData.length, recordingRef.current]);

  return (
    <IonPage>
      {!cameraShown && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%) translateY(20%)',
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/assets/images/BackgroundImage_Record.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -1,
          }}
        />
      )}

      {!cameraShown &&
        (() => {
          const batteryLevel = fsm.batteryLevel.get();
          return (
            <Toolbar
              bleConnected={bleConnected}
              currentUser={currentUser!}
              openDeviceModal={openDeviceModal}
              openDebugModal={_openDebugModal}
              batteryLevel={batteryLevel}
            />
          );
        })()}

      {/* RecordInfoModal immer vor PostRecordModal rendern */}
      <RecordInfoModal
        isOpen={isRecordInfoModalOpen}
        onDidDismiss={() => setRecordInfoModalOpen(false)}
        recordInfo={{
          totalTime: 12345,
          bleConnected: 9000,
          bleDisconnected: 3345,
          sampleCount: 42,
        }}
      />

      {/* ✅ FIXED: Nur rendern wenn Modal tatsächlich gezeigt werden soll */}
      {shouldShowPostRecordModal && baseData && (
        <PostRecordModal
          isOpen={shouldShowPostRecordModal}
          recordData={recordData}
          runBaseData={baseData}
          closeModal={() => setPostRecordModalOpen(false)}
          onOpenRecordInfoModal={() => setRecordInfoModalOpen(true)}
        />
      )}

      <DeviceModal isOpen={deviceModalOpen} closeModal={closeDeviceModal} />

      {_debugModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => _setDebugModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '80%',
              maxHeight: '80%',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Debug Info</h3>
            <pre>{JSON.stringify(_contestDebugInfo, null, 2)}</pre>
            <button onClick={() => _setDebugModalOpen(false)}>Close</button>
          </div>
        </div>
      )}

      <IonContent
        forceOverscroll={false}
        fullscreen
        scrollY={false}
        scrollX={false}
      >
        <div id="frame">
          <div id="content">
            <div className="timerArea animate__animated animate__fadeInDown animate__faster">
              <TimerElement
                frames={timer}
                userInputState={fsm.userInputState.get()}
                videoActive={cameraShown}
              />
            </div>

            {userContests.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100vw',
                  overflowX: 'auto',
                  paddingBottom: 8,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
                className="contest-banner-scroll"
              >
                {userContests.map((contest) => {
                  const isSelected = contestSelection.isSelected(contest.id);
                  const contestStatus = getContestStatus(contest);

                  return (
                    !cameraShown && (
                      <div
                        key={`${contest.id}-${updateTrigger}`}
                        style={{
                          width: '90vw',
                          maxWidth: 600,
                          minWidth: 320,
                          flex: '0 0 90vw',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <ContestBanner
                          contestStatus={contestStatus}
                          isSelected={isSelected}
                          onSelect={() => {
                            logger.info(
                              `👆 Contest ${contest.name} selected: ${contestStatus.runCount}/${contestStatus.maxRuns}`
                            );

                            if (contestStatus.isCompleted) {
                              logger.warn(`⚠️ Contest completed`);
                              present({
                                message: `Contest "${contest.name}" completed! All ${contestStatus.maxRuns} runs used.`,
                                duration: MESSAGE_ALIVE_MS,
                                color: 'warning',
                                position: MESSAGE_POSITION,
                              });
                              return;
                            }

                            contestSelection.selectContest(contest.id);
                          }}
                        />
                      </div>
                    )
                  );
                })}
              </div>
            )}

            <Spacer />
            <InputsUser
              canRecord={canRecord}
              tare={tare}
              toggleRecording={() => toggleRecording()}
              state={fsm.userInputState.get()}
              videoActive={cameraShown}
              toggleVideo={toggleVideo}
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
});

declare global {
  interface Window {
    contestSelectionUpdate?: (
      contestId: string,
      runCount: number,
      maxRuns: number
    ) => void;
  }
}

export default RecordPage;
